import { rgba } from 'polished';
import styled, { css } from 'styled-components';
import { variant } from 'styled-system';
import { textStyles } from '../../../theme/config/typography';
import Typography from '../../styles/Typography';
import { StyledTableRowProps } from './types';

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
`;

export const StyledTableHeadText = styled(Typography).attrs({
  forwardedAs: 'h3',
  variant: 'caption',
})`
  color: ${({ theme }) => rgba(theme.colors.text.secondary, 0.55)};
  padding: 0.875rem 0;
  font-size: 0.6875rem;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

export const StyledTableBody = styled.tbody`
  width: 100%;
`;

export const StyledTableRow = styled.tr<StyledTableRowProps>`
  transition: ${({ theme }) => theme.transitions.default};
  position: relative;

  & > td {
    border-bottom: 1px solid
      ${({ theme }) => rgba(theme.colors.text.primary, 0.06)};
    transition: background-color 180ms cubic-bezier(0.4, 0, 0.2, 1);
  }

  & > td:first-child {
    padding-left: 0.875rem;
    border-top-left-radius: 0.625rem;
    border-bottom-left-radius: 0.625rem;
    position: relative;
  }

  & > td:last-child {
    padding-right: 0.875rem;
    border-top-right-radius: 0.625rem;
    border-bottom-right-radius: 0.625rem;
  }

  & > td:first-child::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    height: 0;
    width: 3px;
    background: ${({ theme }) => theme.colors.accent};
    border-radius: 0 2px 2px 0;
    transition: height 200ms cubic-bezier(0.4, 0, 0.2, 1),
      top 200ms cubic-bezier(0.4, 0, 0.2, 1);
  }

  ${({ clickable, theme }) =>
    clickable &&
    css`
      &:hover {
        cursor: pointer;
      }
      &:hover > td {
        background-color: ${rgba(theme.colors.accent, 0.05)};
      }
      &:hover > td:first-child::before {
        height: 60%;
        top: 20%;
      }
    `}

  &:last-child > td {
    border-bottom: none;
  }
`;

export const StyledTableHead = styled.thead`
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: ${({ theme }) => theme.colors.background.light};

  & > ${StyledTableRow} > th {
    border-bottom: 1px solid
      ${({ theme }) => rgba(theme.colors.text.primary, 0.08)};
    box-shadow: 0 4px 8px -8px rgba(16, 24, 40, 0.16);
  }

  & > ${StyledTableRow}:hover {
    cursor: initial;
    background-color: initial;
  }
`;

export const StyledTableCell = styled.td.attrs({
  variant: 'body',
})`
  vertical-align: middle;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 0.875rem;
  letter-spacing: -0.005em;
  font-variant-numeric: tabular-nums;
  ${variant({
    variants: {
      ...textStyles,
    },
  })};
  &:not(:last-child) {
    padding-right: 1.25rem;
  }
`;

export const StyledTableCellContent = styled.div`
  min-height: 3.25rem;
  padding: 0.875rem 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;
